import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, Button, TextField,
  Alert, Skeleton, Grid, InputAdornment, Avatar, Tooltip, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import {
  SearchOutlined, AddOutlined, EventOutlined,
  DescriptionOutlined, PeopleOutlined,
  LocationOnOutlined, CalendarTodayOutlined,
  GridViewOutlined, ListOutlined, ChevronLeftOutlined, ChevronRightOutlined,
  DeleteOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../hooks/useApi';
import { StatusChip, EVENT_STATUS, fmtDate } from '../../components/DashboardShared';
import { PRIMARY } from './shared';

const LOCATION_LABEL = { virtual: 'Virtual', physical: 'In-Person', hybrid: 'Hybrid' };
const PAGE_SIZE = 12;

function eventLogo(ev) { return ev?.branding?.logoUrl || ev?.logoUrl || null; }
function eventBanner(ev) { return ev?.coverImage || ev?.branding?.bannerUrl || ev?.bannerUrl || null; }

// ── Inline date picker (single month, range-aware) ────────────────────────────
const WEEKDAYS_SHORT = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function calendarGrid(year, month) {
  const dowFirst = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < dowFirst; i++) days.push(null);
  const total = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= total; d++) days.push(new Date(year, month, d));
  return days;
}

function isoDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}

function fmtDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CreateDatePicker({ startDate, endDate, onStartChange, onEndChange, singleDay, onSingleDayChange }) {
  const init = () => {
    const d = startDate ? new Date(startDate) : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  };
  const [view, setView] = useState(init);
  const [hovered, setHovered] = useState(null);
  const [selecting, setSelecting] = useState('start');

  useEffect(() => {
    if (startDate) {
      const d = new Date(startDate);
      setView({ year: d.getFullYear(), month: d.getMonth() });
    }
  }, [startDate]);

  const prev = () => setView(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  const next = () => setView(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 });

  const handleDayClick = (d) => {
    const iso = isoDate(d);
    if (singleDay) {
      onStartChange(iso);
      onEndChange('');
      return;
    }
    if (selecting === 'start') {
      onStartChange(iso);
      if (endDate && iso > endDate) onEndChange('');
      setSelecting('end');
    } else {
      if (iso < startDate) {
        onEndChange(startDate);
        onStartChange(iso);
      } else {
        onEndChange(iso);
      }
      setSelecting('start');
    }
  };

  const rangeEnd = endDate || hovered;
  const grid = calendarGrid(view.year, view.month);
  const todayIso = isoDate(new Date());

  return (
    <Box sx={{ border: '1px solid #e8edf2', borderRadius: 1.5, overflow: 'hidden' }}>

      {/* One-day toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #f0f4f8', background: '#fafbfc' }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>One-day event</Typography>
        <Box
          onClick={() => { onSingleDayChange(!singleDay); if (!singleDay) onEndChange(''); setSelecting('start'); }}
          sx={{
            width: 36, height: 20, borderRadius: 10,
            background: singleDay ? PRIMARY : '#d0d5dd',
            position: 'relative', cursor: 'pointer',
            transition: 'background 0.2s', flexShrink: 0,
          }}
        >
          <Box sx={{
            position: 'absolute', top: 2,
            left: singleDay ? 'calc(100% - 18px)' : 2,
            width: 16, height: 16, borderRadius: '50%',
            background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: 'left 0.2s',
          }} />
        </Box>
      </Box>

      {/* Date tabs */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f4f8' }}>
        <Box
          onClick={() => setSelecting('start')}
          sx={{
            flex: 1, px: 2, py: 1.25, cursor: 'pointer',
            borderBottom: selecting === 'start' ? `2px solid ${PRIMARY}` : '2px solid transparent',
            background: selecting === 'start' ? alpha(PRIMARY, 0.03) : 'transparent',
            transition: 'all 0.12s',
            ...(!singleDay ? { borderRight: '1px solid #f0f4f8' } : {}),
          }}
        >
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: selecting === 'start' ? PRIMARY : '#9ea8b3', mb: 0.25 }}>
            {singleDay ? 'Date' : 'Start date'}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: startDate ? '#1a1a2e' : '#c0c8d4' }}>
            {fmtDisplay(startDate) || 'Select date'}
          </Typography>
        </Box>

        {!singleDay && (
          <Box
            onClick={() => setSelecting('end')}
            sx={{
              flex: 1, px: 2, py: 1.25, cursor: 'pointer',
              borderBottom: selecting === 'end' ? `2px solid ${PRIMARY}` : '2px solid transparent',
              background: selecting === 'end' ? alpha(PRIMARY, 0.03) : 'transparent',
              transition: 'all 0.12s',
            }}
          >
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: selecting === 'end' ? PRIMARY : '#9ea8b3', mb: 0.25 }}>
              End date
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: endDate ? '#1a1a2e' : '#c0c8d4' }}>
              {fmtDisplay(endDate) || 'Select date'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Calendar */}
      <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
        {/* Month nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
          <Box onClick={prev} sx={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px solid #e4e9f0', cursor: 'pointer', '&:hover': { background: '#f5f7fa' } }}>
            <ChevronLeftOutlined sx={{ fontSize: 15, color: '#5a6474' }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>
            {MONTH_NAMES[view.month]} {view.year}
          </Typography>
          <Box onClick={next} sx={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px solid #e4e9f0', cursor: 'pointer', '&:hover': { background: '#f5f7fa' } }}>
            <ChevronRightOutlined sx={{ fontSize: 15, color: '#5a6474' }} />
          </Box>
        </Box>

        {/* Weekday headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.25 }}>
          {WEEKDAYS_SHORT.map(d => (
            <Box key={d} sx={{ textAlign: 'center', py: 0.4 }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#9ea8b3' }}>{d}</Typography>
            </Box>
          ))}
        </Box>

        {/* Day cells */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #f0f4f8', borderRadius: 1, overflow: 'hidden' }}>
          {grid.map((day, i) => {
            if (!day) return <Box key={`b${i}`} sx={{ height: 34, border: '1px solid #f0f4f8', background: '#fafbfc' }} />;
            const iso = isoDate(day);
            const isStart    = iso === startDate;
            const isEnd      = iso === endDate;
            const isSelected = isStart || isEnd;
            const inRange    = !singleDay && startDate && rangeEnd && iso > startDate && iso < rangeEnd;
            const isToday    = iso === todayIso;

            let cellBg = 'transparent';
            if (!singleDay && inRange) cellBg = alpha(PRIMARY, 0.08);
            else if (!singleDay && isStart && endDate) cellBg = `linear-gradient(to right, transparent 50%, ${alpha(PRIMARY, 0.08)} 50%)`;
            else if (!singleDay && isEnd && startDate) cellBg = `linear-gradient(to left, transparent 50%, ${alpha(PRIMARY, 0.08)} 50%)`;

            return (
              <Box
                key={iso}
                onClick={() => handleDayClick(day)}
                onMouseEnter={() => !singleDay && selecting === 'end' && setHovered(iso)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  height: 34,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', position: 'relative',
                  border: '1px solid #f0f4f8',
                  background: cellBg,
                }}
              >
                <Box sx={{
                  width: 30, height: 30,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%',
                  background: isSelected ? PRIMARY : 'transparent',
                  border: isToday && !isSelected ? `1.5px solid ${PRIMARY}` : 'none',
                  zIndex: 1,
                  transition: 'background 0.12s',
                  '&:hover': { background: isSelected ? '#0d5fa8' : alpha(PRIMARY, 0.12) },
                }}>
                  <Typography sx={{
                    fontSize: '0.8rem',
                    fontWeight: isSelected ? 800 : isToday ? 700 : 400,
                    color: isSelected ? '#fff' : inRange ? PRIMARY : '#374151',
                    lineHeight: 1, userSelect: 'none',
                  }}>
                    {day.getDate()}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// ── Create event dialog ───────────────────────────────────────────────────────
function CreateEventDialog({ open, onClose, onCreate }) {
  const [name, setName]           = useState('');
  const [slug, setSlug]           = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [singleDay, setSingleDay] = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const handleNameChange = (v) => {
    setName(v);
    setSlug(v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
  };

  const reset = () => {
    setName(''); setSlug(''); setStartDate(''); setEndDate('');
    setSingleDay(true); setError(''); setSaving(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Event name is required.'); return; }
    if (!slug.trim()) { setError('Slug is required.'); return; }
    if (!startDate)   { setError('Please select a start date.'); return; }
    if (!singleDay && !endDate) { setError('Please select an end date or enable "One-day event".'); return; }
    setSaving(true); setError('');
    try {
      const r = await api.post('/events', {
        name: name.trim(),
        slug: slug.trim(),
        startDate,
        endDate: singleDay ? startDate : endDate,
      });
      onCreate(r.data.data);
      handleClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create event');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 1 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1 }}>Create New Event</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {error && <Alert severity="error" sx={{ borderRadius: 1 }}>{error}</Alert>}

        <TextField
          label="Event Name" value={name} onChange={e => handleNameChange(e.target.value)}
          fullWidth size="small" autoFocus placeholder="e.g. Annual Conference 2026"
        />
        <TextField
          label="Slug" value={slug}
          onChange={e => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
          fullWidth size="small" placeholder="annual-conference-2026"
          helperText="URL-safe identifier — auto-filled from name"
          InputProps={{
            startAdornment: (
              <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8rem', mr: 0.5, whiteSpace: 'nowrap' }}>
                /events/
              </Box>
            ),
          }}
        />

        <CreateDatePicker
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
          singleDay={singleDay}
          onSingleDayChange={setSingleDay}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 1 }}>Cancel</Button>
        <Button
          onClick={handleCreate} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ borderRadius: 1, background: PRIMARY, '&:hover': { background: '#0d5fa8' }, textTransform: 'none' }}
        >
          {saving ? 'Creating…' : 'Create Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete confirmation dialog ────────────────────────────────────────────────
function DeleteEventDialog({ open, event, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState('');

  const handleDelete = async () => {
    setDeleting(true); setError('');
    try {
      await api.delete(`/events/${event._id}`);
      onDeleted(event._id);
      setDeleting(false);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete event');
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 1 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1 }}>Delete Event</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ borderRadius: 1, mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete <strong>{event?.name}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 1 }}>Cancel</Button>
        <Button
          onClick={handleDelete} variant="contained" color="error" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlined sx={{ fontSize: 16 }} />}
          sx={{ borderRadius: 1, textTransform: 'none' }}
        >
          {deleting ? 'Deleting…' : 'Delete Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Event card ────────────────────────────────────────────────────────────────
function EventCard({ ev, onClick, onDeleteClick }) {
  const banner = eventBanner(ev);
  const logo   = eventLogo(ev);
  const hue    = ((ev.name?.charCodeAt(0) || 65) * 13) % 360;

  const stats = [
    { icon: <DescriptionOutlined sx={{ fontSize: 13 }} />, value: ev.formCount ?? 0, label: 'Forms', color: PRIMARY },
    { icon: <PeopleOutlined sx={{ fontSize: 13 }} />, value: ev.participantCount ?? ev.submissionCount ?? 0, label: 'Participants', color: '#00a882' },
  ];

  return (
    <Card onClick={onClick} sx={{
      width: '100%', borderRadius: 1.5, border: '1px solid #e4ecf4',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)', cursor: 'pointer',
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'all 0.18s ease',
      '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
      '&:hover .delete-btn': { opacity: 1 },
    }}>
      <Box sx={{ position: 'relative', flexShrink: 0, height: 130, overflow: 'visible' }}>
        {banner
          ? <Box component="img" src={banner} alt={ev.name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <Box sx={{ width: '100%', height: '100%', background: `linear-gradient(135deg, hsl(${hue},55%,92%), hsl(${hue},40%,96%))`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EventOutlined sx={{ fontSize: 40, color: `hsl(${hue},40%,72%)` }} />
            </Box>
        }
        <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
          <StatusChip status={ev.status} />
        </Box>
        <Box sx={{
          position: 'absolute', bottom: -18, left: 16, zIndex: 2,
          width: 44, height: 44, borderRadius: logo ? 0.5 : '50%',
          background: '#fff', border: '2.5px solid #fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {logo
            ? <Box component="img" src={logo} alt="logo" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: `hsl(${hue},40%,45%)`, lineHeight: 1, textTransform: 'uppercase' }}>{ev.name?.[0]}</Typography>
          }
        </Box>
      </Box>

      <CardContent sx={{ px: 2.5, pt: 3.5, pb: 2, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3, mb: 1 }}>
          {ev.name}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 'auto' }}>
          {ev.startDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CalendarTodayOutlined sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {fmtDate(ev.startDate)}{ev.endDate && ev.endDate !== ev.startDate ? ` — ${fmtDate(ev.endDate)}` : ''}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LocationOnOutlined sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">{LOCATION_LABEL[ev.locationType] || 'Virtual'}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${alpha('#000', 0.06)}`, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
            {stats.map(s => (
              <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Box sx={{ color: s.color }}>{s.icon}</Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: s.color }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Box>
            ))}
          </Box>
          <Tooltip title="Delete event">
            <IconButton
              className="delete-btn"
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteClick(ev); }}
              sx={{
                opacity: 0, transition: 'opacity 0.15s ease',
                color: '#e53935', p: 0.5, borderRadius: 0.5,
                '&:hover': { background: alpha('#e53935', 0.08) },
              }}
            >
              <DeleteOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Create card ───────────────────────────────────────────────────────────────
function CreateEventCard({ onClick }) {
  return (
    <Card onClick={onClick} sx={{
      width: '100%', borderRadius: 1.5,
      border: `2px dashed ${alpha(PRIMARY, 0.3)}`,
      boxShadow: 'none', cursor: 'pointer',
      height: '100%', minHeight: 220,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 1.5,
      background: alpha(PRIMARY, 0.02),
      transition: 'all 0.18s ease',
      '&:hover': {
        borderColor: PRIMARY,
        background: alpha(PRIMARY, 0.05),
        transform: 'translateY(-2px)',
        boxShadow: `0 4px 16px ${alpha(PRIMARY, 0.12)}`,
      },
    }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: 0.5,
        background: alpha(PRIMARY, 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <AddOutlined sx={{ fontSize: 28, color: PRIMARY }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: PRIMARY }}>
        Create New Event
      </Typography>
    </Card>
  );
}

// ── List row ──────────────────────────────────────────────────────────────────
function EventRow({ ev, onClick, onDeleteClick }) {
  const logo = eventLogo(ev);
  const hue  = ((ev.name?.charCodeAt(0) || 65) * 13) % 360;

  return (
    <TableRow hover onClick={onClick} sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 }, '&:hover .delete-btn': { opacity: 1 } }}>
      <TableCell sx={{ py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, flexShrink: 0,
            borderRadius: logo ? 0.5 : '50%',
            overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: logo ? '#f8f9fb' : `hsl(${hue},55%,92%)`,
            border: '1px solid #e4ecf4',
          }}>
            {logo
              ? <Box component="img" src={logo} alt="logo" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: `hsl(${hue},40%,45%)`, textTransform: 'uppercase' }}>{ev.name?.[0]}</Typography>
            }
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{ev.name}</Typography>
            <Typography variant="caption" color="text.secondary">/{ev.slug}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell><StatusChip status={ev.status} /></TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {ev.startDate ? fmtDate(ev.startDate) : '—'}
          {ev.endDate && ev.endDate !== ev.startDate ? ` — ${fmtDate(ev.endDate)}` : ''}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">{LOCATION_LABEL[ev.locationType] || 'Virtual'}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>{ev.formCount ?? 0}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#00a882' }}>
          {ev.participantCount ?? ev.submissionCount ?? 0}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Tooltip title="Delete event">
          <IconButton
            className="delete-btn"
            size="small"
            onClick={(e) => { e.stopPropagation(); onDeleteClick(ev); }}
            sx={{
              opacity: 0, transition: 'opacity 0.15s ease',
              color: '#e53935', borderRadius: 0.5,
              '&:hover': { background: alpha('#e53935', 0.08) },
            }}
          >
            <DeleteOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, total, pageSize, onChange }) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 4 }}>
      <IconButton size="small" disabled={page === 1} onClick={() => onChange(page - 1)}
        sx={{ border: '1px solid #e4e9f0', borderRadius: 0.5 }}>
        <ChevronLeftOutlined sx={{ fontSize: 18 }} />
      </IconButton>

      {Array.from({ length: pages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 1)
        .reduce((acc, p, i, arr) => {
          if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
          acc.push(p);
          return acc;
        }, [])
        .map((p, i) => p === '…'
          ? <Typography key={`e${i}`} sx={{ px: 0.5, color: 'text.secondary', fontSize: '0.85rem' }}>…</Typography>
          : (
            <Box key={p} onClick={() => onChange(p)} sx={{
              width: 32, height: 32, borderRadius: 0.5, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: p === page ? PRIMARY : 'transparent',
              border: `1px solid ${p === page ? PRIMARY : '#e4e9f0'}`,
              color: p === page ? '#fff' : '#374151',
              fontWeight: p === page ? 700 : 400, fontSize: '0.85rem',
              transition: 'all 0.12s',
              '&:hover': p !== page ? { background: '#f5f7fa' } : {},
            }}>{p}</Box>
          )
        )
      }

      <IconButton size="small" disabled={page === pages} onClick={() => onChange(page + 1)}
        sx={{ border: '1px solid #e4e9f0', borderRadius: 0.5 }}>
        <ChevronRightOutlined sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function EventsList() {
  const navigate = useNavigate();
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [error, setError]           = useState('');
  const [view, setView]             = useState('card');
  const [page, setPage]             = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get('/events')
      .then(r => setEvents(r.data.data || []))
      .catch(e => setError(e?.response?.data?.message || 'Failed to load events'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [search]);

  const filtered = events.filter(e =>
    !search ||
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const goTo = (ev) => navigate(`/editor/${ev._id}/details`);
  const handleDeleted = (id) => setEvents(prev => prev.filter(e => e._id !== id));

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800 }}>Events</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${filtered.length} event${filtered.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search events…" size="small" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ width: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
          />
          <Box sx={{ display: 'flex', border: '1px solid #e4e9f0', borderRadius: 0.5, overflow: 'hidden' }}>
            <Tooltip title="Card view">
              <IconButton size="small" onClick={() => setView('card')}
                sx={{ borderRadius: 0, px: 1.25, background: view === 'card' ? alpha(PRIMARY, 0.08) : 'transparent', color: view === 'card' ? PRIMARY : '#9ea8b3' }}>
                <GridViewOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="List view">
              <IconButton size="small" onClick={() => setView('list')}
                sx={{ borderRadius: 0, px: 1.25, background: view === 'list' ? alpha(PRIMARY, 0.08) : 'transparent', color: view === 'list' ? PRIMARY : '#9ea8b3' }}>
                <ListOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Button variant="contained" startIcon={<AddOutlined />}
            onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 0.5, background: PRIMARY, '&:hover': { background: '#0d5fa8' }, textTransform: 'none' }}>
            New Event
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 0.5 }}>{error}</Alert>}

      <CreateEventDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={(ev) => {
          setEvents(prev => [ev, ...prev]);
          navigate(`/editor/${ev._id}/details`);
        }}
      />

      <DeleteEventDialog
        open={!!deleteTarget}
        event={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />

      {loading ? (
        view === 'card' ? (
          <Grid container spacing={2.5}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} key={i} sx={{ display: 'flex' }}>
                <Card sx={{ borderRadius: 0.5, flex: 1 }}>
                  <Skeleton variant="rectangular" height={130} />
                  <Box sx={{ p: 2.5 }}>
                    <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                    <Skeleton width="40%" height={14} sx={{ mb: 0.75 }} />
                    <Skeleton width="30%" height={14} />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ border: '1px solid #e8edf2', borderRadius: 0.5, overflow: 'hidden' }}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mx: 2, my: 0.5, borderRadius: 0.5 }} />)}
          </Box>
        )
      ) : filtered.length === 0 ? (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
            <CreateEventCard onClick={() => setCreateOpen(true)} />
          </Grid>
        </Grid>
      ) : view === 'card' ? (
        <>
          <Grid container spacing={2.5} alignItems="stretch">
            {paginated.map(ev => (
              <Grid item xs={12} sm={6} key={ev._id} sx={{ display: 'flex' }}>
                <EventCard ev={ev} onClick={() => goTo(ev)} onDeleteClick={setDeleteTarget} />
              </Grid>
            ))}
            {page === Math.ceil(filtered.length / PAGE_SIZE) && (
              <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                <CreateEventCard onClick={() => setCreateOpen(true)} />
              </Grid>
            )}
          </Grid>
          <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      ) : (
        <>
          <Box sx={{ border: '1px solid #e8edf2', borderRadius: 0.5, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f8f9fb' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Dates</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }} align="center">Forms</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }} align="center">Participants</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }} align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map(ev => (
                    <EventRow key={ev._id} ev={ev} onClick={() => goTo(ev)} onDeleteClick={setDeleteTarget} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      )}
    </Box>
  );
}